import { Link, useLocation } from "react-router-dom";
import { Phone, MapPin, Clock, Mail } from "lucide-react";
import autosurLogo from "@/assets/autosur-logo-full.webp";
import btoLogo from "@/assets/b-to-logo.webp";
import { TrustvilleBadge } from "./TrustvilleBadge";

export const SiteFooter = () => {
  const location = useLocation();

  return (
    <footer className="bg-autosur-navy text-white">

      {/* Main footer */}
      <div className="container mx-auto px-6 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
          {/* Brand column */}
          <div className="lg:col-span-3">
            <Link 
              to="/"
              onClick={(e) => {
                if (location.pathname === "/") {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
            >
              <img 
                src={autosurLogo} 
                alt="Autosur" 
                className="h-10 w-auto brightness-0 invert mb-6 hover:opacity-80 transition-opacity"
              />
            </Link>
            <p className="font-body text-white/70 text-sm leading-relaxed mb-6">
              Votre centre de contrôle technique agréé à Montreuil-sous-Bois. 
              Expertise et qualité pour tous types de véhicules depuis plus de 20 ans.
            </p>
            <TrustvilleBadge />
          </div>

          {/* Quick links */}
          <div className="lg:col-span-2">
            <h3 className="font-heading font-bold text-white mb-6">Navigation</h3>
            <ul className="space-y-3">
              {[
                { href: "/", label: "Accueil" },
                { href: "/le-centre", label: "Le Centre" },
                { href: "/prestations", label: "Nos Prestations" },
                { href: "/faq", label: "FAQ" },
                { href: "/blog", label: "Blog" },
                { href: "/contact", label: "Contact" },
              ].map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href}
                    onClick={(e) => {
                      if (link.href === "/" && location.pathname === "/") {
                        e.preventDefault();
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }
                    }}
                    className="font-body text-white/70 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Vehicles */}
          <div className="lg:col-span-3">
            <h3 className="font-heading font-bold text-white mb-6">Nos Véhicules</h3>
            <ul className="space-y-3">
              {[
                { href: "/vehicules/voitures", label: "Voitures particulières" },
                { href: "/vehicules/motos", label: "Motos & 2 Roues" },
                { href: "/vehicules/collection", label: "Véhicules de collection" },
                { href: "/vehicules/utilitaires", label: "Utilitaires & Camping-cars" },
              ].map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href}
                    className="font-body text-white/70 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

            <h3 className="font-heading font-bold text-white mt-6 mb-4 text-sm">Villes proches</h3>
            <ul className="space-y-2">
              {[
                { href: "/controle-technique-montreuil", label: "CT Montreuil" },
                { href: "/controle-technique-romainville", label: "CT Romainville" },
                { href: "/controle-technique-les-lilas", label: "CT Les Lilas" },
                { href: "/controle-technique-bagnolet", label: "CT Bagnolet" },
                { href: "/controle-technique-saint-mande", label: "CT Saint-Mandé" },
                { href: "/controle-technique-vincennes", label: "CT Vincennes" },
                { href: "/controle-technique-noisy-le-sec", label: "CT Noisy-le-Sec" },
                { href: "/controle-technique-rosny-sous-bois", label: "CT Rosny-sous-Bois" },
                { href: "/controle-technique-fontenay-sous-bois", label: "CT Fontenay-sous-Bois" },
                { href: "/controle-technique-bondy", label: "CT Bondy" },
                { href: "/controle-technique-pas-cher-93", label: "CT pas cher 93" },
              ].map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href}
                    className="font-body text-white/60 hover:text-white/80 transition-colors text-xs"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact info */}
          <div className="lg:col-span-4">
            <h3 className="font-heading font-bold text-white mb-6">Contact</h3>
            <ul className="space-y-4">
              <li>
                <a 
                  href="tel:+33149889240"
                  className="flex items-start gap-3 group"
                >
                  <Phone className="w-5 h-5 text-autosur-red flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-body text-white group-hover:text-white/80 transition-colors text-sm">01 49 88 92 40</p>
                    <p className="text-white/60 text-xs">Appel gratuit</p>
                  </div>
                </a>
              </li>
              <li>
                <a 
                  href="mailto:autosur.montreuil@gmail.com"
                  className="flex items-start gap-3 group"
                >
                  <Mail className="w-5 h-5 text-autosur-red flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-body text-white group-hover:text-white/80 transition-colors text-sm break-all">autosur.montreuil@gmail.com</p>
                  </div>
                </a>
              </li>
              <li>
              <a 
                  href="https://maps.app.goo.gl/QMbfid7TsPVr8ZB76"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 group"
                >
                  <MapPin className="w-5 h-5 text-autosur-red flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-body text-white group-hover:text-white/80 transition-colors text-sm">133, Bd Aristide Briand</p>
                    <p className="text-white/60 text-xs">93100 Montreuil-sous-Bois</p>
                  </div>
                </a>
              </li>
              <li>
                <a 
                  href="https://controle-technique.autosur.fr/550-autosur-montreuil-sous-bois#in-nav-rdv"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 group"
                >
                  <Clock className="w-5 h-5 text-autosur-red flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-body text-white group-hover:text-white/80 transition-colors text-sm">Lun-Ven: 8h30-18h30</p>
                    <p className="text-white/60 text-xs">Sam: 9h-13h | Dim: Fermé</p>
                  </div>
                </a>
              </li>
            </ul>

          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="font-body text-white/60 text-sm text-center md:text-left">
              © 2026 Autosur Montreuil-sous-Bois. Tous droits réservés.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/mentions-legales" className="font-body text-white/60 hover:text-white/80 text-sm transition-colors">
                Mentions légales
              </Link>
              <Link to="/politique-cookies" className="font-body text-white/60 hover:text-white/80 text-sm transition-colors">
                Politique cookies
              </Link>
              <Link to="/politique-confidentialite" className="font-body text-white/60 hover:text-white/80 text-sm transition-colors">
                Confidentialité
              </Link>
            </div>
            <a 
              href="https://b-to.io/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 font-body text-white/60 hover:text-white/80 text-sm transition-colors"
            >
              Site réalisé par
              <img src={btoLogo} alt="B-to" className="h-7 w-auto" width={486} height={197} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
